import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { Lock, Loader2, AlertCircle } from 'lucide-react';
import { authService } from './services/authService';
import AuthLayout from './components/auth/AuthLayout';
import AuthCard from './components/auth/AuthCard';
import Button from './components/ui/Button';
import logger, { safeErrorEventDetails } from './utils/logger';
import { MIN_PASSWORD_LENGTH } from './config/constants';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const requestVersion = useRef(0);
  useLayoutEffect(() => {
    requestVersion.current += 1;
    setStatus('checking');
    setError('');
    setPassword('');
    setConfirmPassword('');
    return () => { requestVersion.current += 1; };
  }, [location.key, token]);

  useEffect(() => {
    const version = ++requestVersion.current;
    const isCurrentRequest = () => version === requestVersion.current;
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        return;
      }
      try {
        await authService.validateResetToken(token);
        if (!isCurrentRequest()) return;
        setStatus('ready');
      } catch (err) {
        if (!isCurrentRequest()) return;
        logger.errorEvent('password_reset_token_invalid', safeErrorEventDetails(err));
        setStatus('invalid');
      }
    };
    validateToken();
    return () => { requestVersion.current += 1; };
  }, [location.key, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const version = ++requestVersion.current;
    const isCurrentRequest = () => version === requestVersion.current;
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      logger.errorEvent('password_reset_invalid_input', { field: 'password', reason: 'min_length' });
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      logger.errorEvent('password_reset_invalid_input', { field: 'password', reason: 'missing_complexity' });
      setError('Password must contain uppercase, lowercase, and number');
      return;
    }
    if (password !== confirmPassword) {
      logger.errorEvent('password_reset_invalid_input', { field: 'confirmPassword', reason: 'mismatch' });
      setError('Passwords do not match');
      return;
    }

    setStatus('loading');
    try {
      await authService.resetPassword(token, password, confirmPassword);
      if (!isCurrentRequest()) return;
      navigate('/reset-success');
    } catch (err) {
      if (!isCurrentRequest()) return;
      logger.errorEvent('password_reset_failed', safeErrorEventDetails(err));
      setError(err.message || 'Reset failed');
      setStatus('ready');
    }
  };

  return (
    <AuthLayout>
        <AuthCard>
          {status === 'checking' && (
            <div className="auth-heading">
              <Loader2 className="mx-auto mb-4 h-16 w-16 animate-spin text-[var(--color-text-primary)]" />
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Validating link...</h2>
            </div>
          )}

          {status === 'invalid' && (
            <div className="auth-heading">
              <div className="auth-status-icon failure">
                <AlertCircle className="h-10 w-10 text-[var(--color-text-primary)]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Invalid or expired link</h2>
              <p className="mb-6 text-[var(--color-text-secondary)]">Please request a new reset link.</p>
              <Button
                onClick={() => navigate('/forgot-password')}
              >
                Request New Link
              </Button>
            </div>
          )}

          {(status === 'ready' || status === 'loading') && (
            <>
              <div className="auth-heading mb-6">
                <div className="auth-status-icon">
                  <Lock className="h-10 w-10 text-[var(--color-text-primary)]" />
                </div>
                <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Reset Password</h2>
                <p className="text-[var(--color-text-secondary)]">Enter a new password for your account.</p>
              </div>

              {error && (
                <div className="auth-error" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="reset-new" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">New Password</label>
                  <input
                    id="reset-new" type="password" autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reset-confirm" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">Confirm Password</label>
                  <input
                    id="reset-confirm" type="password" autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  loading={status === 'loading'}
                  loadingLabel="Updating password…"
                  fullWidth
                >
                  Update Password
                </Button>
              </form>
            </>
          )}
        </AuthCard>
    </AuthLayout>
  );
}

export default ResetPassword;
